import { FC, JSX, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavigateFunction, useLocation, useNavigate } from "react-router";
import axios, { AxiosResponse } from "axios";
import dayjs, { Dayjs } from "dayjs";
import {
  periodicUploadingStaticFilters,
  sourceUploadingStaticFilters,
  StaticFiltersType
} from "../TableFilters/utils/staticFilters";

// redux
import { AppDispatch } from "redux/store/store";
import { AppStateType } from "redux/reducers/mainReducer";
import { deleteImportTask, fetchAllImportTasks } from "redux/thunks/importTask.thunk";
import {
  fetchAllImportTasksSuccess,
  InitialImportTaskStateType,
  setCurrentTable,
  TabModeType
} from "redux/slices/importTaskSlice";
import { APP_PATHES } from "constants/appPathes";

// utils
import generateSorter from "utils/generateSorter";
import useCancelableDebounce from "hooks/useCancelableDebounce";
import { formatDateRange } from "utils/formatDateRange";
import { handleError } from "utils/handleError";

// components
import { Dropdown, FormInstance, MenuProps, notification, Segmented } from "antd";
import CatalogTable, { CatalogColumnType } from "components/Catalog/CatalogTable/CatalogTable";
import EmptyBlock from "components/EmptyBlock/EmptyBlock";
import InfoCard from "components/ui/InfoCard/InfoCard";
import UploadingProductsFiltersPanel from "./UploadingProductsFiltersPanel/UploadingProductsFiltersPanel";
import RangePicker from "components/ui/RangePicker/RangePicker";
import PrimaryButton from "components/ui/PrimaryButton/PrimaryButton";
import ConfirmModal from "components/ui/modals/ConfirmModal/ConfirmModal";

// icons
import { ReactComponent as MenuIcon } from "assets/icons/three_dots_icon.svg";
import { ReactComponent as LoupeIcon } from "assets/icons/search-square-icon.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close-icon.svg";

// types
import {
  ImportTaskFilterInfoResponseType,
  ImportTaskType,
  PaginatedImportTasksRequestType,
  TasksStatusType
} from "importTask/types";
import { SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import { RequestPaginationType } from "app/types";
import { RangePickerProps } from "antd/es/date-picker";
import { getTasksStatus } from "api/importTask";

interface IUploadingProductsTabProps {
  form: FormInstance;
}

type DatesFilterType = {
  periodic: {
    createdAt: [dayjs.Dayjs, dayjs.Dayjs] | null,
    updatedAt: [dayjs.Dayjs, dayjs.Dayjs] | null,
  },
  source: [dayjs.Dayjs, dayjs.Dayjs] | null
}

const importTasksColumns: CatalogColumnType[] = [
  "created_at", "updated_at", "periodicity",
  "status", "source", "file", "actions"
];
const sourcesColumns: CatalogColumnType[] = ["date_time", "catalogue_type", "status", "source", "file", "actions"];

const UploadingProductsTab: FC<IUploadingProductsTabProps> = (): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate: NavigateFunction = useNavigate();

  const importTask: InitialImportTaskStateType = useSelector((state: AppStateType) => state.importTaskSlice);
  const filterInfo: ImportTaskFilterInfoResponseType = useSelector((state: AppStateType) =>
    state.importTaskSlice)?.filterInfo;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowInfoCard, setIsShowInfoCard] = useState<boolean>(
    localStorage.getItem("uploadingProductsInfo") !== "true"
  );
  const [taskToDelete, setTaskToDelete] = useState<string>(null);
  const [sorterInfo, setSorterInfo] = useState<SorterResult<any> | SorterResult<any>[]>(null);
  const [paginationInfo, setPaginationInfo] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    locale: {
      items_per_page: "/ на странице",
    },
  });
  const [periodicStaticFilters, setPeriodicStaticFilters] = useState<StaticFiltersType[]>(
    periodicUploadingStaticFilters
  );
  const [sourcesStaticFilters, setSourcesStaticFilters] = useState<StaticFiltersType[]>(
    sourceUploadingStaticFilters
  );
  const [dateFilter, setDateFilter] = useState<DatesFilterType>({
    periodic: {
      createdAt: null,
      updatedAt: null,
    },
    source: null
  });
  const [isFiltersConfirmed, setIsFiltersConfirmed] = useState<boolean>(false);

  const getIsPeriodic = (): string => {
    const searchParams: URLSearchParams = new URLSearchParams(location.search);

    return searchParams.get("is_periodic");
  };

  const updateTable = (table: "importTasks" | "sources", isPeriodicValue: boolean): void => {
    fetchImportTasks({
      ...getRequestPagination(),
      is_periodic: isPeriodicValue,
    });

    dispatch(setCurrentTable(table));
  };

  useEffect(() => {
    fetchImportTasks({
      ...getRequestPagination(),
      is_periodic: importTask?.currentTable === "importTasks",
      ...getRequestFilters(),
      ...(sorterInfo ? { order_by: generateSorter(sorterInfo) } : {}),
    });
  }, [paginationInfo.current, paginationInfo.pageSize, sorterInfo]);

  useEffect(() => {
    const updateFilters = (filters: StaticFiltersType[]): StaticFiltersType[] => filters
      .map((f: StaticFiltersType) => {
        const count: number = filterInfo.status[f.name] ?? 0;

        return {
          ...f,
          count,
          isDisabled: count === 0,
        };
      });

    if (filterInfo) {
      if (importTask?.currentTable === "importTasks") {
        setPeriodicStaticFilters((prev: StaticFiltersType[]) => updateFilters(prev));
      }

      if (importTask?.currentTable === "sources") {
        setSourcesStaticFilters((prev: StaticFiltersType[]) => updateFilters(prev));
      }
    }
  }, [filterInfo]);

  useEffect(() => {
    if (isFiltersConfirmed) {
      setIsFiltersConfirmed(false);

      fetchImportTasks({
        page: 1,
        page_size: paginationInfo.pageSize,
        is_periodic: importTask?.currentTable === "importTasks",
        ...getRequestFilters(),
        ...(sorterInfo ? { order_by: generateSorter(sorterInfo) } : {}),
      });
    }
  }, [isFiltersConfirmed]);

  useEffect(() => {
    const isPeriodicParam: string = getIsPeriodic();
    const hasParam: boolean = isPeriodicParam !== null;
    const isPeriodic: boolean = hasParam && isPeriodicParam === "true";
    const tableVariant: "importTasks" | "sources" = isPeriodic ? "importTasks" : "sources";
    const desiredTable: "importTasks" | "sources" = hasParam
     ? tableVariant
     : "importTasks";

    const currentTable: TabModeType = importTask?.currentTable;

    if (currentTable !== desiredTable) {
      updateTable(desiredTable, isPeriodic);
    }

    return () => {
      updateTable(currentTable, true);
    };
  }, [location.search]);

  const { debouncedFunction: fetchImportTasks } = useCancelableDebounce((params, token) => {
    setIsLoading(true);

    dispatch(fetchAllImportTasks(params, token))
      .catch((error: any) => {
        if (!axios.isCancel(error)) {
          handleError("Ошибка при загрузке списка задач.", error);
        }
      })
      .finally(() => setIsLoading(false));
  });

  useEffect(() => {
    let isMounted: boolean = true;
    let isChecking: boolean = false;

    const checkAndUpdateImportTasks = async (): Promise<void> => {
      const statusesToTrack: string[] = ["Редактирование", "В загрузке", "Новый"];
      const pendingTasks: ImportTaskType[] = importTask?.list?.filter(
       ({ status }) => statusesToTrack.includes(status)
      );

      if (!pendingTasks || pendingTasks.length === 0) return;

      const taskIds: string[] = pendingTasks.map(({ id }) => id);

      const checkStatuses = async (): Promise<void> => {
        try {
          await new Promise(resolve => setTimeout(resolve, 10000));

          if (!isMounted) return;

          if (isChecking) return;
          isChecking = true;

          const updatedTasks: AxiosResponse<TasksStatusType[]> = await getTasksStatus(taskIds);

          const hasChanges: boolean = pendingTasks.some(({ id, status }) => {
            const updatedTask: TasksStatusType = updatedTasks.data?.find(
             (task: TasksStatusType): boolean => task.id === id
            );

            return updatedTask?.status !== status;
          });

          if (hasChanges) {
            const currentTable: TabModeType = importTask?.currentTable;
            const isPeriodicParam: string = getIsPeriodic();
            const hasParam: boolean = isPeriodicParam !== null;
            const isPeriodic: boolean = hasParam && isPeriodicParam === "true";

            updateTable(currentTable, isPeriodic);
          }

          isChecking = false;

          await checkStatuses();
        } catch (error) {
          console.error("Ошибка проверки статусов:", error);
          isChecking = false;

          await checkStatuses();
        }
      };

      if (taskIds.length) {
        checkStatuses();
      }
    };

    if (importTask && importTask.list?.length) {
      checkAndUpdateImportTasks();
    }

    return () => {
      isMounted = false;
    };
  }, [importTask]);

  // Модальное окно на удаление задачи
  const onOkDeleteHandler = (): void => {
    handlerDeleteImportTask(taskToDelete);
  };

  const closeDeleteModal = (): void => {
    setTaskToDelete(null);
  };

  const handlerDeleteImportTask = (taskId: string): void => {
    dispatch(deleteImportTask({ import_task_id: taskId }))
      .then(() => {
        notification.success({ message: "Каталог успешно удален", description: "" });
        closeDeleteModal();
      })
      .catch((error) => {
        handleError("Ошибка при удалении каталога", error);
        closeDeleteModal();
      });
  };

  const getRequestPagination = (): RequestPaginationType => ({
    page: paginationInfo.current,
    page_size: paginationInfo.pageSize,
  });

  const getRequestFilters = (): PaginatedImportTasksRequestType => {
    const getActiveStatuses = (filters: StaticFiltersType[]) => filters.filter(({ isActive }) => isActive);

    const getDates = (): Record<string, string> => {
      if (importTask?.currentTable === "importTasks") {
        return {
          ...(formatDateRange(["created_at_from", "created_at_to"], dateFilter.periodic.createdAt)),
          ...(formatDateRange(["updated_at_from", "updated_at_to"], dateFilter.periodic.updatedAt)),
        };
      } else {
        return { ...(formatDateRange(["created_at_from", "created_at_to"], dateFilter.source)) };
      }
    };

    const statuses: string[] = importTask?.currentTable === "importTasks"
      ? getActiveStatuses(periodicStaticFilters)?.map(({ name }) => name)
      : getActiveStatuses(sourcesStaticFilters)?.map(({ name }) => name);

    return {
      ...(statuses?.length ? { status: statuses } : {}),
      ...getDates()
    };
  };

  const renderEmptyBlock = (): JSX.Element => {
    const handleOnClick = (): void => navigate(APP_PATHES.IMPORT_MASTER);

    return (
      <EmptyBlock
        icon={<LoupeIcon className="mb-4" />}
        style={{ height: "calc(100vh - 35rem)", minHeight: "400px" }}
        title="Загрузок нет"
        description={<>Ранее вы не загружали товары, загрузите их сейчас</>}
        btn={<PrimaryButton onClickHandler={handleOnClick} text="Загрузить товары" />}
      />
    );
  };

  const renderUploadAutoActions = (entity: ImportTaskType, idx: number): React.ReactNode => {
    const { id, status, source } = entity;

    const isShowPause: boolean = status !== "Остановлено"
      && status !== "Успешно"
      && status !== "Ошибка";

    const items: MenuProps["items"] = [
      {
        label: <a>Редактировать</a>,
        onClick: () => handleEdit(id, source),
        key: `edit-${idx}`,
      },
      // TODO скрываем до реализации функционала
      // importTask?.currentTable === "importTasks" && {
      //   label: <a onClick={() => { }}>{isShowPause ? "Остановить" : "Запустить"}</a>,
      //   key: `pause-${idx}`,
      //   disabled: true
      // },
      {
        label: <a style={{ color: "#FF4D4F" }}>Удалить</a>,
        onClick: () => setTaskToDelete(id),
        key: `delete-${idx}`,
      },
    ];

    return (
      <Dropdown menu={{ items }} trigger={["hover"]}>
        <MenuIcon className="m-auto cursor-pointer secondary-color" />
      </Dropdown>
    );
  };

  const handleEdit = (id: string, source: string): void => {
    if (source === "YML") {
      navigate(`${APP_PATHES.IMPORT_MASTER_YML}/${id}?edit`);
    } else {
      navigate(`${APP_PATHES.IMPORT_MASTER_EXCEL}/${id}?edit`);
      dispatch(fetchAllImportTasksSuccess({ results: null, total: 0, page: 1, page_size: 10 }));
      dispatch(fetchAllImportTasksSuccess(null));
    }
  };

  const renderTable = (): JSX.Element => {
    return (
      <CatalogTable
        columns={importTask?.currentTable === "importTasks" ? importTasksColumns : sourcesColumns}
        list={importTask.list}
        count={importTask.count}
        isFetching={isLoading}
        isShowEmptyBlock={!isLoading}
        emptyBlock={renderEmptyBlock()}
        renderActions={renderUploadAutoActions}
        setPaginationInfo={setPaginationInfo}
        setSorterInfo={setSorterInfo}
        onClickHandler={handleEdit}
      />
    );
  };

  const handleTabChange = (value: TabModeType): void => {
    const params: URLSearchParams = new URLSearchParams(location.search);

    params.set("is_periodic", value === "importTasks" ? "true" : "false");

    const newUrl: string = `${location.pathname}?${params.toString()}`;

    window.history.replaceState({}, "", newUrl);

    fetchImportTasks({
      ...getRequestPagination(),
      is_periodic: value === "importTasks",
      page: 1,
    });

    setPeriodicStaticFilters(periodicUploadingStaticFilters);
    setSourcesStaticFilters(sourceUploadingStaticFilters);

    dispatch(setCurrentTable(value));
  };

  const hideInfoCard = (): void => {
    localStorage.setItem("uploadingProductsInfo", "true");
    setIsShowInfoCard(false);
  };

  const disableDate = (current: dayjs.Dayjs, minDate: dayjs.Dayjs, maxDate: dayjs.Dayjs): boolean =>
    current > dayjs() || current < minDate.startOf("day") || current > maxDate.endOf("day");

  const handleChangeSourcesDate = (dates: [Dayjs | null, Dayjs | null]): void => {
    setDateFilter((prev) => ({ ...prev, source: dates }));
    setIsFiltersConfirmed(true);
  };

  const handleChangePeriodDate = (dates: [Dayjs | null, Dayjs | null], type: "createdAt" | "updatedAt"): void => {
    setDateFilter((prev) => {
      return {
        ...prev,
        periodic: { ...prev.periodic, [type]: dates },
      };
    });

    setIsFiltersConfirmed(true);
  };

  const confirmText: string = importTask?.currentTable === "importTasks"
    ? "Вы действительно хотите удалить выбранное автообновление по ссылке?"
    : "Каталог будет удален без возможности восстановления";

  return (
    <div>
      {isShowInfoCard && (
        <InfoCard
          text={{
            content: `Вы можете настроить автоматическое обновление каталога ваших товаров, цен и остатков.
              Для этого необходимо загрузить ссылку на YML каталог и выбрать опцию периодической загрузки.`
          }}
          actionBtn={<CloseIcon onClick={hideInfoCard} className="cursor-pointer" />}
          icon={{ type: "exclamation" }}
        />
      )}
      <div className="flex">
        <Segmented
          options={[
            { label: "Автообновление", value: "importTasks" },
            { label: "Разовые загрузки", value: "sources" },
          ]}
          onChange={handleTabChange}
          value={importTask?.currentTable}
          size="large"
          className="mr-3"
        />
        {importTask?.currentTable === "sources" && (
          <RangePicker
            minDate={dayjs(filterInfo?.created_at_from)}
            maxDate={dayjs(filterInfo?.created_at_to)}
            disabledDate={(currentDate: dayjs.Dayjs) => disableDate(
              currentDate,
              dayjs(filterInfo?.created_at_from),
              dayjs(filterInfo?.created_at_to)
            )}
            handleChange={handleChangeSourcesDate}
          />
        )}
        {importTask?.currentTable === "importTasks" && (
          <div className="flex w-full gap-3">
            <RangePicker
              minDate={dayjs(filterInfo?.created_at_from)}
              maxDate={dayjs(filterInfo?.created_at_to)}
              placeholder={["С даты создания", "По дату"]}
              tooltipTitle="Фильтр по дате создания автообновления"
              disabledDate={(currentDate: dayjs.Dayjs) => disableDate(
                currentDate,
                dayjs(filterInfo?.created_at_from),
                dayjs(filterInfo?.created_at_to)
              )}
              handleChange={(dates: [Dayjs | null, Dayjs | null]) => handleChangePeriodDate(dates, "createdAt")}
            />
            <RangePicker
              minDate={dayjs(filterInfo?.updated_at_from)}
              maxDate={dayjs(filterInfo?.updated_at_to)}
              placeholder={["С даты обновления", "По дату"]}
              tooltipTitle="Фильтр по дате обновления"
              disabledDate={(currentDate: dayjs.Dayjs) => disableDate(
                currentDate,
                dayjs(filterInfo?.updated_at_from),
                dayjs(filterInfo?.updated_at_to)
              )}
              handleChange={(dates: [Dayjs | null, Dayjs | null]) => handleChangePeriodDate(dates, "updatedAt")}
            />
          </div>
        )}
      </div>
      <UploadingProductsFiltersPanel
        periodicStaticFilters={periodicStaticFilters}
        sourcesStaticFilters={sourcesStaticFilters}
        setPeriodicStaticFilters={setPeriodicStaticFilters}
        setSourcesStaticFilters={setSourcesStaticFilters}
        setIsFiltersConfirmed={setIsFiltersConfirmed}
      />
      {renderTable()}
      <ConfirmModal
        isOpen={!!taskToDelete}
        text={confirmText}
        onOkHandler={onOkDeleteHandler}
        onCancelHandler={closeDeleteModal} />
    </div>
  );
};

export default UploadingProductsTab;
